import { addDisplayName, addProps, flowMax } from 'ad-hok'
import { take, shuffle, uniqBy } from 'lodash/fp'
import { FC } from 'react'
import { CreatorFeatureWithUsernameAndImage } from 'shared/src/api/types'
import { addPropIdentityStabilization } from 'ad-hok-utils'
import { flow } from 'lodash'
import { createUseStyles } from 'react-jss'

import { addTranslationHelpers } from 'utils/addTranslationHelpers'
import { makeFluidCreatorUrl } from 'utils/routes'
import { sansSerifFontFamily, serifFontFamily } from 'utils/styles'
import { LAYOUT_MAX_WIDTH } from 'utils/layout'
import { FollowButton } from 'components/feed/profile/FollowButton'
import { LinkOrButton } from 'components/LinkOrButton'

const useStyles = createUseStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    maxWidth: LAYOUT_MAX_WIDTH,
    position: 'relative',
    width: '100%',
    marginTop: -49,
  },
  featuredText: {
    fontFamily: serifFontFamily,
    color: '#FFF',
    fontSize: 32,
    fontWeight: 700,
    lineHeight: '129.5%',
    letterSpacing: 0.32,
    marginLeft: 32,
    marginBottom: 51,
  },
  verticalText: {
    fontFamily: sansSerifFontFamily,
    fontWeight: 600,
    fontSize: 6.517,
    lineHeight: '140%',
    letterSpacing: 1.336,
    display: 'flex',
    justifyContent: 'center',
    transform: 'rotate(-180deg)',
    writingMode: 'vertical-lr',
    color: '#FFF',
    height: 232,
    marginLeft: 10,
    marginRight: 20,
  },
  horizontalContainer: {
    display: 'flex',
    marginBottom: 25,
  },
  scrollableContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    overflowX: 'scroll',
    height: '50%',
    gap: 16,
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
    paddingRight: 32,
  },
  featureContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  image: {
    width: 197,
    height: 232,
  },
  followButtonContainer: {
    backgroundColor: '#232323',
    marginLeft: 16,
    marginRight: 16,
    marginTop: 12,
    marginBottom: 12,
    borderRadius: 4,
  },
})

const randomizeFeatures: (
  features: CreatorFeatureWithUsernameAndImage[]
) => CreatorFeatureWithUsernameAndImage[] = flow(
  shuffle,
  uniqBy('userId'),
  take(6)
)

interface Props {
  creatorFeatures: CreatorFeatureWithUsernameAndImage[]
}

export const FeedFeaturedCreator: FC<Props> = flowMax(
  addDisplayName('CreatorFeatures'),
  addPropIdentityStabilization('creatorFeatures'),
  addProps(
    ({ creatorFeatures }) => ({
      features: randomizeFeatures(creatorFeatures),
    }),
    ['creatorFeatures']
  ),
  addTranslationHelpers,
  addProps(() => ({
    classes: useStyles(),
  })),
  ({ features, classes, t }) => (
    <div className={classes.container}>
      <div className={classes.featuredText}>{t('feed.featuredCreators')}</div>
      <div className={classes.horizontalContainer}>
        <div className={classes.verticalText}>{t('feed.featuredCreators')}</div>
        <div className={classes.scrollableContainer}>
          {features.map((feature, index) => (
            <div key={index} className={classes.featureContainer}>
              <LinkOrButton
                navigateTo={makeFluidCreatorUrl(feature.username)}
                showBackButton
                additionalState={{ discoverySource: 'feedFeature' }}
              >
                <img
                  src={feature.image.full}
                  alt={feature.username ?? t('feed.featuredCreators')}
                  className={classes.image}
                />
              </LinkOrButton>
              <FollowButton
                creatorId={feature.userId}
                className={classes.followButtonContainer}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  )
)
